/**
 * WHOSE ROWS THE TWO SA LISTS HOLD, in words (SEC-002, C-10).
 *
 * Provider transactions and SA reconciliation list only the rows of the
 * reader's brands, and each answer says whether its reader is restricted to
 * some brands (`brandRestricted`). Every sentence here is built from that
 * answer, so a restricted reader is never told a list is every callback, and
 * never reads an empty one as a zero for every brand. Until the first answer
 * is here (null) nothing is said about breadth either way: the notices are on
 * screen before the list has answered, and stay there if it fails.
 */

const IN_YOUR_BRANDS = 'in the brands you can see';

const RECORDED = 'the provider’s txnid, the operation, the amount and what we did with it.';

/** The opening sentence of the Provider transactions notice. */
export function transactionsLead(brandRestricted: boolean | null): string {
  if (brandRestricted === null)
    return `The wallet callbacks a live provider sent, as we recorded them: ${RECORDED}`;
  return brandRestricted
    ? `Every wallet callback a live provider sent for a player ${IN_YOUR_BRANDS}, as we recorded it: ${RECORDED}`
    : `Every wallet callback a live provider sent, as we recorded it: ${RECORDED}`;
}

/** Provider transactions' empty state, for an answer that held no rows. */
export function transactionsEmpty(brandRestricted: boolean): { title: string; body: string } {
  const failed = 'not a screen that failed to load — the message above says so when it did.';
  return brandRestricted
    ? {
        title: `No provider transactions ${IN_YOUR_BRANDS}`,
        body:
          `Nothing ${IN_YOUR_BRANDS} matches these filters. That is a real count of nothing ` +
          `in those brands, ${failed}`,
      }
    : {
        title: 'No provider transactions',
        body: `Nothing matches these filters. That is a real count of nothing, ${failed}`,
      };
}

/**
 * The opening of the SA reconciliation notice. reconcile-sa is scheduled every
 * ten minutes (services/worker/src/jobs/reconcile-sa.ts), not nightly.
 */
export function reconciliationLead(brandRestricted: boolean | null, provider = 'SA'): string {
  const check =
    provider === 'JILI'
      ? 'Where JILI’s hourly totals, or a callback of JILI’s we could not honour, disagree with ours'
      : 'Where the check against SA’s own records, run every ten minutes, disagrees with ours';
  return brandRestricted === true
    ? `${check}, for players ${IN_YOUR_BRANDS}. A finding that cannot be linked to one of our ` +
        'players is shown only to staff who see every brand.'
    : `${check}.`;
}

/**
 * The check behind the reconciliation list, as its answer reports it: whether the provider is
 * connected on this deployment, and the check's last finished run (failed or not), or null when it
 * has never finished one.
 */
export interface ReconciliationCheck {
  connected: boolean;
  last: { finishedAt: string; ok: boolean } | null;
}

/** What the empty open list needs to say when the check last ran. */
export interface CheckWords {
  /** The provider's name as the screen writes it ("SA Gaming", "JILI"). */
  providerName: string;
  check: ReconciliationCheck;
  /** The formatter the screen's other times use, so the time reads in Manila time like them. */
  formatWhen: (iso: string) => string;
}

/**
 * The reconciliation list's empty state, for an answer that held no findings.
 *
 * AN EMPTY OPEN LIST IS A CLEAN RESULT ONLY IF THE CHECK RAN. It used to say "This is a real zero"
 * whatever the check had done, and the JILI list said so on a platform where JILI's check had never
 * run at all (module walk, 30 Oct 2026). So the open list says when the check last finished a run,
 * or that it has not, or that its last run failed, or that the provider is not connected here — and
 * never "real zero". The resolved list holds people's own closures, read from our own records, so its
 * zero is a real one and says so.
 */
export function reconciliationEmpty(
  brandRestricted: boolean,
  status: string,
  oneReason: boolean,
  words: CheckWords,
): { title: string; body: string } {
  const where = brandRestricted ? ` ${IN_YOUR_BRANDS}` : '';
  const findings = `No ${status === 'OPEN' ? 'open' : 'resolved'} findings${oneReason ? ' of this reason' : ''}${where}`;
  const clean = { title: `Nothing to reconcile${where}` };
  if (status !== 'OPEN')
    return { ...clean, body: `${findings}. This is a real zero${brandRestricted ? ' for those brands' : ''}.` };

  const { providerName, check, formatWhen } = words;
  const theCheck = `the check against ${providerName}`;
  if (!check.connected)
    return {
      title: `${providerName} is not connected`,
      body: `${findings}. ${providerName} is not connected on this platform, so its check has nothing to compare.`,
    };
  if (check.last === null)
    return {
      title: 'Nothing has been compared yet',
      body: `${findings}, but ${theCheck} has not finished a run yet, so nothing has been compared. This is not a clean result.`,
    };
  if (!check.last.ok)
    return {
      title: 'The last check failed',
      body: `${findings}, but ${theCheck} failed on its last run, on ${formatWhen(check.last.finishedAt)}. This is not a clean result.`,
    };
  return {
    ...clean,
    body: `${findings}. The check against ${providerName} last finished a run on ${formatWhen(check.last.finishedAt)}.`,
  };
}
